import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Send, Shield, Award, CheckCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data/mock';
import { openConsentSettings } from '../lib/consent';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleQuickQuote = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/quick-enquiry`, { email });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Why Us', href: '/#why-us' },
    { name: 'Reviews', href: '/#reviews' },
    { name: 'Get Quote', href: '/get-quote' }
  ];

  const serviceLinks = [
    { name: 'House Removals Brighton', href: '/house-removals-brighton' },
    { name: 'Man and Van Brighton', href: '/man-and-van-brighton' },
    { name: 'Office Removals Brighton', href: '/office-removals-brighton' },
    { name: 'Packing Materials', href: '/packing-materials-brighton' }
  ];

  const areaLinks = [
    { name: 'Removals Hove', href: '/removals-hove' },
    { name: 'Removals East Sussex', href: '/removals-east-sussex' }
  ];

  const usefulLinks = [
    { name: 'Blog / Moving Tips', href: '/blog' },
    { name: 'Partners', href: '/partners' }
  ];

  return (
    <footer id="contact" className="bg-gray-900 text-white">
      {/* Trust Badges Bar */}
      <div className="bg-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">Fully Insured</p>
                <p className="text-xs text-gray-400">Comprehensive cover</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">Google Guaranteed</p>
                <p className="text-xs text-gray-400">Verified business</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">Brighton &amp; Hove Based</p>
                <p className="text-xs text-gray-400">Local, experienced team</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_98b7cebb-f063-4be0-8c8c-dc4674976523/artifacts/vwcvgsh8_Happyvan%20white.avif" 
                alt="HappyVan Logo" 
                className="w-44 h-auto object-contain"
                width="176"
                height="60"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-stone-400 mb-6 leading-relaxed">
              Professional furniture removals and delivery services in Brighton, Sussex, and the South Coast.
            </p>
            
            {/* Mini Quote Form */}
            <div className="bg-stone-800 rounded-xl p-4">
              <p className="text-sm font-semibold text-white mb-3">Get a Quick Callback</p>
              {isSubmitted ? (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>We'll be in touch!</span>
                </div>
              ) : (
                <form onSubmit={handleQuickQuote} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-sm text-white placeholder-stone-400 focus:outline-none focus:border-[#3B43AF]"
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg transition-colors"
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-stone-400 hover:text-amber-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-[#3B43AF] rounded-full group-hover:bg-amber-400 transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-white">Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-stone-400 hover:text-amber-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-[#3B43AF] rounded-full group-hover:bg-amber-400 transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Areas We Cover */}
            <h4 className="font-heading text-lg font-semibold mb-4 mt-8 text-white">Areas We Cover</h4>
            <ul className="space-y-3">
              {areaLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-stone-400 hover:text-amber-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-[#3B43AF] rounded-full group-hover:bg-amber-400 transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Useful Info */}
            <h4 className="font-heading text-lg font-semibold mb-4 mt-8 text-white">Useful Info</h4>
            <ul className="space-y-3">
              {usefulLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-stone-400 hover:text-amber-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-[#3B43AF] rounded-full group-hover:bg-amber-400 transition-colors"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start gap-3 text-stone-400 hover:text-amber-400 transition-colors group"
                >
                  <div className="w-10 h-10 bg-stone-800 group-hover:bg-[#3B43AF] rounded-lg flex items-center justify-center transition-colors">
                    <Phone className="w-5 h-5 text-[#5B63CF] group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 mb-0.5">Call us</p>
                    <span className="font-medium text-white">{companyInfo.phone}</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-start gap-3 text-stone-400 hover:text-amber-400 transition-colors group"
                >
                  <div className="w-10 h-10 bg-stone-800 group-hover:bg-[#3B43AF] rounded-lg flex items-center justify-center transition-colors">
                    <Mail className="w-5 h-5 text-[#5B63CF] group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 mb-0.5">Email us</p>
                    <span className="font-medium text-white">{companyInfo.email}</span>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-stone-400">
                <div className="w-10 h-10 bg-stone-800 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#5B63CF]" />
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-0.5">Location</p>
                  <span className="font-medium text-white">{companyInfo.location}</span>
                </div>
              </li>
              <li className="flex items-start gap-3 text-stone-400">
                <div className="w-10 h-10 bg-stone-800 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#5B63CF]" />
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-0.5">Hours</p>
                  <p className="font-medium text-white">Mon - Sat: 8am - 6pm</p>
                </div>
              </li>
            </ul>

            {/* Social Links */}
            <div className="mt-6">
              <p className="text-sm text-stone-500 mb-3">Follow us:</p>
              <div className="flex gap-3">
                <a 
                  href={companyInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="HappyVan on Facebook"
                  className="w-10 h-10 bg-stone-800 hover:bg-[#3B43AF] rounded-lg flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
                  data-testid="footer-facebook-link"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href={companyInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="HappyVan on Instagram"
                  className="w-10 h-10 bg-stone-800 hover:bg-[#3B43AF] rounded-lg flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
                  data-testid="footer-instagram-link"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a 
                  href={companyInfo.social.googleBusinessProfile}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="HappyVan on Google"
                  className="w-10 h-10 bg-stone-800 hover:bg-[#3B43AF] rounded-lg flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
                  data-testid="footer-google-link"
                >
                  <Star className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-stone-500 text-sm">
              © {currentYear} HappyVan. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/privacy" className="text-stone-500 hover:text-amber-400 transition-colors">Privacy Policy</Link>
              <button
                type="button"
                onClick={openConsentSettings}
                className="text-stone-500 hover:text-amber-400 transition-colors"
                data-testid="footer-cookie-settings-btn"
              >
                Cookie Settings
              </button>
              <Link to="/prize-draw-terms" className="text-stone-500 hover:text-amber-400 transition-colors" data-testid="footer-prize-draw-terms-link">Prize Draw T&amp;Cs</Link>
              <Link to="/terms" className="text-stone-500 hover:text-amber-400 transition-colors">Terms of Service</Link>
            </div>
          </div>
          
          {/* Agency Credit */}
          <div className="mt-4 pt-4 border-t border-white/5 text-center">
            <p className="text-stone-600 text-sm">
              Website designed & built by{' '}
              <a 
                href="https://www.happinessinaction.co" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#5B63CF] hover:text-amber-400 transition-colors font-medium"
              >
                Happiness in Action
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
